import { useEffect, useRef } from "react";
import { EditorView, basicSetup } from "codemirror";
import { EditorState } from "@codemirror/state";
import { javascript } from "@codemirror/lang-javascript";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { markdown } from "@codemirror/lang-markdown";

// CodeMirror 6 with syntax highlighting only (no AI autocomplete, PRD §5.1).

function language(path: string) {
  if (path.endsWith(".js")) return javascript();
  if (path.endsWith(".html")) return html();
  if (path.endsWith(".css")) return css();
  if (path.endsWith(".md")) return markdown();
  return [];
}

export function Editor({ path, value, onChange, readOnly, line }: { path: string; value: string; onChange: (v: string) => void; readOnly?: boolean; line?: { n: number; nonce: number } | null }) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const external = useRef(false);

  useEffect(() => {
    const v = new EditorView({
      parent: host.current!,
      state: EditorState.create({
        doc: value,
        extensions: [
          basicSetup,
          language(path),
          EditorView.lineWrapping,
          EditorState.readOnly.of(!!readOnly),
          EditorView.updateListener.of((u) => {
            if (u.docChanged && !external.current) onChangeRef.current(u.state.doc.toString());
          }),
        ],
      }),
    });
    view.current = v;
    return () => v.destroy();
    // A new file or read-only change rebuilds the editor.
  }, [path, readOnly]);

  // Changes from outside (the agent, a restore) replace the text without remounting.
  useEffect(() => {
    const v = view.current;
    if (v && v.state.doc.toString() !== value) {
      external.current = true;
      v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: value } });
      external.current = false;
    }
  }, [value]);

  // "Show all code" from a card: put the cursor on the card's first line and scroll to it.
  useEffect(() => {
    const v = view.current;
    if (!v || !line) return;
    const n = Math.min(Math.max(1, line.n), v.state.doc.lines);
    const pos = v.state.doc.line(n).from;
    v.dispatch({ selection: { anchor: pos }, effects: EditorView.scrollIntoView(pos, { y: "start", yMargin: 24 }) });
  }, [line?.nonce, path]);

  return <div className="editor" ref={host} />;
}
